import useDeclarations from 'hooks/useDeclarations';
import { useEffect, useMemo, useState } from 'react';
import { rejectedStatuses } from 'views/declarations/common/declaration-errors-drawer/shouldShowNotificationErrors';
import getLatestSubmittedDeclaration from './getLatestSubmittedDeclaration';

/**
 * The declaration as its latest submission sent it, for a rejected declaration. Customs names a rejected
 * item by its position in the submission, and items deleted since have moved the ones after them up -
 * so the positions are read against the copy the history kept.
 *
 * `isHistoryLoaded` says whether the history request has settled: until then the positions could still
 * be read against the wrong items. A failed request leaves no copy, and callers fall back to the
 * current items.
 */
const useSubmittedDeclaration = () => {
    const { declaration, declarationHistory, getDeclarationHistory } = useDeclarations();
    const [isHistoryLoaded, setIsHistoryLoaded] = useState(false);

    const isRejected = !!declaration?.status && rejectedStatuses.includes(declaration.status);

    useEffect(() => {
        if (!isRejected || !declaration?.id) return;

        setIsHistoryLoaded(false);
        Promise.resolve(getDeclarationHistory(declaration.id))
            .catch(() => undefined)
            .finally(() => setIsHistoryLoaded(true));
    }, [isRejected, declaration?.id, getDeclarationHistory]);

    const submittedDeclaration = useMemo(
        () => getLatestSubmittedDeclaration(declarationHistory, declaration?.id),
        [declarationHistory, declaration?.id]
    );

    // Only a rejected declaration waits for the history - for any other there is nothing to load.
    return { submittedDeclaration, isHistoryLoaded: !isRejected || isHistoryLoaded };
};

export default useSubmittedDeclaration;
